import { useCallback, useEffect, useRef, useState } from "react";

import ACCOUNT_SERVICES from "./accountService";
import parseAccountError from "./accountErrors";
import {
  EMPTY_CODES,
  isChallengeComplete,
  toConfirmPayload,
  toOtpChallenge,
} from "./accountMappers";
import { OTP_LENGTH, RESEND_COOLDOWN_SECONDS } from "../auth/authPolicy";

const useDeactivateAccount = ({ active, onClosed }) => {
  const [challenge, setChallenge] = useState(null);
  const [codes, setCodes] = useState(EMPTY_CODES);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  const mounted = useRef(true);
  const closed = useRef(onClosed);

  closed.current = onClosed;

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!active || cooldown <= 0) return undefined;

    const timer = setInterval(
      () => setCooldown((current) => current - 1),
      1000
    );

    return () => clearInterval(timer);
  }, [active, cooldown]);

  const start = useCallback(async () => {
    setStarting(true);
    setError("");
    setCodes(EMPTY_CODES);

    try {
      const { data } = await ACCOUNT_SERVICES.deactivationInitiate();

      if (!mounted.current) return;

      setChallenge(toOtpChallenge(data));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (caught) {
      if (!mounted.current) return;

      const parsed = parseAccountError(caught);

      setChallenge(null);
      setError(parsed.message);

      if (parsed.clearsSession) closed.current?.({ signedOut: true });
    } finally {
      if (mounted.current) setStarting(false);
    }
  }, []);

  useEffect(() => {
    if (!active) {
      setChallenge(null);
      setCodes(EMPTY_CODES);
      setError("");
      return;
    }

    start();
  }, [active, start]);

  const setCode = useCallback((channel, code) => {
    setError("");
    setCodes((current) => ({ ...current, [channel]: code }));
  }, []);

  const submit = useCallback(async () => {
    const sessionId = challenge?.sessionId;
    const targets = challenge?.targets ?? [];

    if (!sessionId || !targets.length) return;

    setSubmitting(true);
    setError("");

    try {
      const { data } = await ACCOUNT_SERVICES.deactivationConfirm(
        toConfirmPayload(sessionId, targets, codes)
      );

      closed.current?.({
        deactivated: true,
        message: data?.message || "Your account has been deactivated.",
      });
    } catch (caught) {
      if (!mounted.current) return;

      const parsed = parseAccountError(caught);

      setError(parsed.message);
      setSubmitting(false);

      if (parsed.clearsSession) {
        closed.current?.({ signedOut: true });
        return;
      }

      if (parsed.needsRestart) {
        setCodes(EMPTY_CODES);
        setChallenge((current) =>
          current ? { ...current, sessionId: null } : current
        );
        setCooldown(0);
      }
    }
  }, [challenge, codes]);

  const targets = challenge?.targets ?? [];
  const expired = Boolean(challenge) && !challenge.sessionId;

  return {
    targets,
    codes,
    setCode,
    message: challenge?.message ?? null,
    starting,
    submitting,
    error,
    cooldown,
    expired,
    canResend: cooldown <= 0 && !starting && !submitting,
    canSubmit:
      !expired &&
      !submitting &&
      !starting &&
      isChallengeComplete(targets, codes, OTP_LENGTH),
    resend: () => {
      if (cooldown > 0 || starting || submitting) return;
      start();
    },
    submit,
    retry: start,
  };
};

export default useDeactivateAccount;
